import { toast } from 'sonner';
import type { InvoiceDeliveryOutcome } from '@/types/invoice';

/**
 * The single place the app turns an invoice `delivery` outcome into a toast.
 *
 * Issuing an invoice and delivering it are separate steps server-side: the
 * status transition commits first, delivery runs after, and the email/SMS
 * providers report a rejection by *returning* a failure rather than throwing.
 * A 200 therefore means "the server did the work", not "the customer got it" —
 * so every send-shaped action reports what `delivery` actually says instead of
 * a blanket success. Both send and reminder route through here so the two can
 * never drift into telling the operator different stories.
 */
export interface DeliveryToast {
  tone: 'success' | 'warning' | 'error';
  title: string;
  description?: string;
}

export interface DeliveryReportCopy {
  /** The server committed the record but never attempted a send. */
  notAttempted: DeliveryToast;
  /** The contact has opted out — a deliberate no-send, not a failure. */
  suppressed: DeliveryToast;
  /** The provider rejected it. `delivery.error` overrides the description. */
  failed: DeliveryToast;
  delivered: DeliveryToast;
}

export const INVOICE_SEND_DELIVERY_COPY: DeliveryReportCopy = {
  notAttempted: { tone: 'success', title: 'Invoice issued' },
  suppressed: {
    tone: 'warning',
    title: 'Invoice issued, but not sent',
    description: 'This customer has opted out of messages, so nothing was delivered.',
  },
  failed: {
    tone: 'error',
    title: 'Invoice issued, but delivery failed',
    description: 'The invoice was issued but could not be delivered to the customer.',
  },
  delivered: { tone: 'success', title: 'Invoice sent successfully' },
};

export const INVOICE_REMINDER_DELIVERY_COPY: DeliveryReportCopy = {
  notAttempted: {
    tone: 'warning',
    title: 'Reminder not sent',
    description: 'Nothing was delivered to the customer.',
  },
  suppressed: {
    tone: 'warning',
    title: 'Reminder not sent',
    description: 'This customer has opted out of messages, so nothing was delivered.',
  },
  failed: {
    tone: 'error',
    title: 'Reminder failed to send',
    description: 'The reminder could not be delivered to the customer.',
  },
  delivered: { tone: 'success', title: 'Invoice reminder sent' },
};

export function reportDelivery(
  delivery: InvoiceDeliveryOutcome | undefined,
  copy: DeliveryReportCopy = INVOICE_SEND_DELIVERY_COPY,
) {
  // Order matters: a suppressed send reports `delivered: true` (an opt-out is
  // not a delivery failure), so `suppressed` has to be read first.
  const outcome: DeliveryToast = !delivery?.attempted
    ? copy.notAttempted
    : delivery.suppressed
      ? copy.suppressed
      : !delivery.delivered
        ? { ...copy.failed, description: delivery.error ?? copy.failed.description }
        : copy.delivered;

  if (outcome.description) {
    toast[outcome.tone](outcome.title, { description: outcome.description });
  } else {
    toast[outcome.tone](outcome.title);
  }
}
